import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/context/AppContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import CustomerBooking from "./pages/customer/CustomerBooking";
import OwnerLayout from "./components/schedora/OwnerLayout";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import OwnerDashboard from "./pages/owner/OwnerDashboard";
import OwnerCalendar from "./pages/owner/OwnerCalendar";
import OwnerStaff from "./pages/owner/OwnerStaff";
import OwnerClockIn from "./pages/owner/OwnerClockIn";
import OwnerServices from "./pages/owner/OwnerServices";
import OwnerClients from "./pages/owner/OwnerClients";
import OwnerAnalytics from "./pages/owner/OwnerAnalytics";
import OwnerSettings from "./pages/owner/OwnerSettings";
import StaffLayout from "./components/staff/StaffLayout";
import StaffDashboard from "./pages/staff/StaffDashboard";
import StaffSchedule from "./pages/staff/StaffSchedule";
import StaffAcceptInvite from "./pages/staff/StaffAcceptInvite";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBusinesses from "./pages/admin/AdminBusinesses";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminBookings from "./pages/admin/AdminBookings";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AppProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/staff/accept" element={<StaffAcceptInvite />} />

            <Route element={<ProtectedRoute role="client" />}>
              <Route path="/customer/dashboard" element={<CustomerDashboard />} />
              <Route path="/customer/booking" element={<CustomerBooking />} />
            </Route>

            <Route element={<ProtectedRoute role="owner" />}>
              <Route path="/owner" element={<OwnerLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<OwnerDashboard />} />
                <Route path="calendar" element={<OwnerCalendar />} />
                <Route path="staff" element={<OwnerStaff />} />
                <Route path="clock-in" element={<OwnerClockIn />} />
                <Route path="services" element={<OwnerServices />} />
                <Route path="clients" element={<OwnerClients />} />
                <Route path="analytics" element={<OwnerAnalytics />} />
                <Route path="settings" element={<OwnerSettings />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute role="staff" />}>
              <Route path="/staff" element={<StaffLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<StaffDashboard />} />
                <Route path="schedule" element={<StaffSchedule />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute role="super_admin" />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="businesses" element={<AdminBusinesses />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="bookings" element={<AdminBookings />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AppProvider>
  </QueryClientProvider>
);

export default App;
